import { keepPreviousData, useQuery, useQueryClient } from '@tanstack/react-query'
import { useEffect, useMemo, useState, type FormEvent } from 'react'
import { ApiError } from '../../api/client'
import { type ContractsTableRequest } from '../../api/settingsContracts'
import {
  assignTemplateService,
  contractTemplatesQuery,
  contractTemplatesShellQuery,
  disableContractTemplate,
  saveContractTemplate,
  templateServicesQuery,
  type ContractTemplateRow,
  type ContractTypeOption,
} from '../../api/settingsContractTemplates'
import { DataTable, useTableState, type TableColumn, type TableState } from '../../ui/DataTable'
import { Forbidden } from '../../ui/Forbidden'
import { Modal } from '../../ui/Modal'
import { toast } from '../../ui/notify'
import { Spinner } from '../../ui/Spinner'
import './ContractTemplatesPage.css'

/**
 * Settings -> Contract Templates (`settings/contracts_templates`).
 *
 * Element cu element fata de
 * `views/version-3/settings/customersV2/contracts_templates/index.php`,
 * `edit_form.php` si `services_assigned.php`:
 *
 *  - lista cu numele, descrierea, cate servicii are, starea si cele doua
 *    butoane (Edit, Disable);
 *  - fereastra "Add New Template", cu tabelul de servicii de bifat;
 *  - randul desfasurat, cu formularul de editare si lista de servicii, fiecare
 *    cu comutatorul lui de legat / dezlegat.
 *
 * CE E ALTFEL
 * -----------
 * RANDUL DESFASURAT N-ARE FILE. In portal, `tabs()` intorcea doua file - "Edit
 * Contract Template" si "Services Assigned" - dar `ul.nav-tabs` din macheta e
 * `style="display:none;"`, iar `edit_form.php` incarca oricum
 * `services_assigned.php` sub el. Adica filele nu se vedeau si amandoua se
 * desenau una sub alta. Aici asta e scris pe fata, fara bara ascunsa.
 *
 * FARA POARTA DE DREPTURI, ca in portal - vezi capul lui
 * `App\Settings\Contracts\ContractTemplates`.
 */
const TEMPLATES_TABLE = 'settings-contract-templates'

const TEMPLATE_SERVICES_TABLE = 'settings-contract-template-services'

const ORDER_NAMES = ['', 'name', 'description', 'count_services', 'status', '']

function failed(error: unknown): string {
  return error instanceof ApiError && error.message ? error.message : 'Something went wrong.'
}

function esc(value: string): string {
  return value.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;')
}

function tableRequest(state: TableState, names: string[], fallback: string): ContractsTableRequest {
  return {
    start: state.start,
    length: state.length,
    orderColumn: state.order.column,
    orderName: names[state.order.column] ?? fallback,
    orderDir: state.order.dir,
    search: state.search,
  }
}

function columns(): TableColumn[] {
  return [
    {
      index: 0,
      title: '',
      orderable: false,
      visible: true,
      width: 30,
      html: (row) =>
        `<center class="show_details color-dark-grey pointer font-16" data-id="${Number(row.ID)}"><i class="fas fa-plus-circle"></i></center>`,
    },
    { index: 1, title: 'Name', orderable: true, visible: true, className: 'a-left', html: (row) => esc(String(row.name ?? '')) },
    {
      index: 2,
      title: 'Description',
      orderable: true,
      visible: true,
      className: 'a-left',
      html: (row) => esc(String(row.description ?? '')),
    },
    {
      index: 3,
      title: 'Assigned Services',
      orderable: true,
      visible: true,
      className: 'a-left',
      html: (row) => {
        const count = Number(row.count_services ?? 0)
        return `<span class="label ${count > 0 ? 'label-success' : 'label-default'}">${count}</span>`
      },
    },
    {
      index: 4,
      title: 'Status',
      orderable: true,
      visible: true,
      className: 'a-left',
      html: (row) =>
        String(row.status) === '1'
          ? '<span class="label label-success">Active</span>'
          : '<span class="label label-danger">Inactive</span>',
    },
    {
      index: 5,
      title: 'Actions',
      orderable: false,
      visible: true,
      width: 130,
      className: 'a-right',
      /* Ca in `contracts_templates/index.php` (coloana Actions): Edit e un
         `center.show_details`, adica acelasi declansator ca plusul din
         prima coloana - deschide sub rand formularul de editare. Lipsea, si
         omul avea doar plusul, care nu spune ca acolo se editeaza. */
      html: (row) =>
        `<center data-id="${Number(row.ID)}" class="btn btn-xs btn-primary show_details pull-left"><i class="fas fa-pencil-alt" aria-hidden="true"></i> Edit </center>` +
        `<a href="javascript:void(0)" data-id="${Number(row.ID)}" class="btn btn-xs btn-danger od-disable-template pull-left" style="margin-left:6px"><i class="fas fa-times" aria-hidden="true"></i> Disable</a>`,
    },
  ]
}

export function ContractTemplatesPage() {
  const query = useQueryClient()
  const shell = useQuery(contractTemplatesShellQuery)
  const [creating, setCreating] = useState(false)
  const [state, setState] = useTableState(TEMPLATES_TABLE, {
    start: 0,
    length: 25,
    /* `order: [[1, 'desc']]` din `contractsTemplatesSettings.initDatatable`. */
    order: { column: 1, dir: 'desc' },
    search: '',
  })
  const table = useQuery({
    ...contractTemplatesQuery(tableRequest(state, ORDER_NAMES, 'name')),
    placeholderData: keepPreviousData,
  })
  const cols = useMemo(() => columns(), [])

  const reload = () => void query.invalidateQueries({ queryKey: ['settings', 'contracts_templates'] })

  if (shell.error instanceof ApiError && shell.error.status === 403) return <Forbidden />
  if (!shell.data) return <Spinner />

  const rows = (table.data?.data ?? []) as unknown as Record<string, unknown>[]

  return (
    <div id="content" className="od-contract-templates">
      <button type="button" className="pull-right btn btn-success btn-sm" onClick={() => setCreating(true)}>
        <i className="fas fa-plus" aria-hidden="true"></i> Create Template
      </button>
      <h4>
        <strong>Contracts Templates</strong>
      </h4>
      <label className="color" style={{ marginBottom: 0 }}>
        Manage, create or edit <strong>templates</strong>
      </label>
      <hr />

      <div
        onClick={(event) => {
          const target = event.target as HTMLElement
          const disable = target.closest('.od-disable-template')
          if (!disable) return
          const id = Number(disable.getAttribute('data-id') ?? 0)
          if (id < 1 || !confirm('Are you sure? This contract template will be disabled')) return
          void disableContractTemplate(id)
            .then(() => {
              toast('Contract removed.', 'Success')
              reload()
            })
            .catch((error: unknown) => toast(failed(error), 'Error', 'error'))
        }}
      >
        <DataTable
          id={TEMPLATES_TABLE}
          columns={cols}
          rows={rows}
          total={table.data?.recordsFiltered ?? 0}
          fetching={table.isFetching}
          error={table.error ? table.error.message : null}
          onRetry={() => void table.refetch()}
          state={state}
          onState={setState}
          rowId={(row) => String(row.DT_RowId ?? `row_${Number(row.ID)}`)}
          emptyHint="A template is the set of services a contract starts from."
          details={(row) => (
            <TemplateDetails
              template={(table.data?.data ?? []).find((item) => item.ID === Number(row.ID)) ?? null}
              contractTypes={shell.data.contractTypes}
              onSaved={reload}
            />
          )}
        />
      </div>

      <NewTemplateModal
        open={creating}
        contractTypes={shell.data.contractTypes}
        onClose={() => setCreating(false)}
        onSaved={() => {
          setCreating(false)
          reload()
        }}
      />
    </div>
  )
}

/** Randul desfasurat: formularul de editare plus lista de servicii. */
function TemplateDetails({
  template,
  contractTypes,
  onSaved,
}: {
  template: ContractTemplateRow | null
  contractTypes: ContractTypeOption[]
  onSaved(): void
}) {
  const [name, setName] = useState(template?.name ?? '')
  const [description, setDescription] = useState(template?.description ?? '')
  const [typeId, setTypeId] = useState(template?.contract_type ?? 0)
  const [status, setStatus] = useState(template?.status ?? '1')

  useEffect(() => {
    setName(template?.name ?? '')
    setDescription(template?.description ?? '')
    setTypeId(template?.contract_type ?? 0)
    setStatus(template?.status ?? '1')
  }, [template])

  if (template === null) return null

  const submit = (event: FormEvent) => {
    event.preventDefault()
    void saveContractTemplate(template.ID, {
      name: name.trim(),
      description,
      contract_type: typeId,
      status,
    })
      .then(() => {
        toast('Edit successfully.', 'Success')
        onSaved()
      })
      .catch((error: unknown) => toast(failed(error), 'Error', 'error'))
  }

  return (
    <div className="row">
      <div className="col-md-12">
        <form method="post" onSubmit={submit}>
          <div className="row">
            <div className="col-md-6">
              <div className="col-md-12">
                <div className="form-group">
                  <label className="control-label">
                    Name <span className="required">*</span>
                  </label>
                  <input
                    type="text"
                    className="form-control input-sm"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    required
                  />
                </div>
              </div>
              <div className="clearfix"></div>
              <div className="col-md-9">
                <div className="form-group">
                  <label className="control-label">
                    Contract Type<span className="required">*</span>
                  </label>
                  <select
                    className="form-control input-sm"
                    value={typeId}
                    onChange={(e) => setTypeId(Number(e.target.value))}
                  >
                    <option value={0}> - select - </option>
                    {contractTypes.map((item) => (
                      <option key={item.ID} value={item.ID}>
                        {item.name}
                      </option>
                    ))}
                  </select>
                </div>
              </div>
              <div className="col-md-3">
                <div className="form-group">
                  <label className="control-label">
                    Status <span className="required">*</span>
                  </label>
                  <select className="form-control input-sm" value={status} onChange={(e) => setStatus(e.target.value)}>
                    <option value="1">Active</option>
                    <option value="0">Inactive</option>
                  </select>
                </div>
              </div>
            </div>
            <div className="col-md-6">
              <div className="col-md-12">
                <div className="form-group">
                  <label className="control-label">Description</label>
                  <textarea
                    className="form-control input-sm"
                    rows={5}
                    value={description}
                    onChange={(e) => setDescription(e.target.value)}
                  />
                </div>
              </div>
            </div>
            <div className="clearfix"></div>
            <div className="col-md-12">
              <div className="form-group center-block">
                <button type="submit" className="btn btn-success center-block" disabled={name.trim() === '' || typeId < 1}>
                  Save
                </button>
              </div>
            </div>
          </div>
        </form>

        <TemplateServices templateId={template.ID} onChanged={onSaved} />
      </div>
    </div>
  )
}

/** `services_assigned.php`: serviciile, cu comutatorul "Assign". */
function TemplateServices({ templateId, onChanged }: { templateId: number; onChanged(): void }) {
  const query = useQueryClient()
  const [state, setState] = useTableState(TEMPLATE_SERVICES_TABLE, {
    start: 0,
    length: 25,
    order: { column: 0, dir: 'asc' },
    search: '',
  })
  const table = useQuery({
    ...templateServicesQuery(templateId, tableRequest(state, ['service_name'], 'service_id')),
    placeholderData: keepPreviousData,
  })

  const rows = table.data?.data ?? []

  const toggle = (serviceId: number, on: boolean) => {
    void assignTemplateService(templateId, serviceId, on)
      .then(() => {
        toast('Update successfully', 'Success')
        void query.invalidateQueries({ queryKey: ['settings', 'contracts_templates', 'services', templateId] })
        onChanged()
      })
      .catch((error: unknown) => toast(failed(error), 'Error', 'error'))
  }

  return (
    <div className="row">
      <div className="col-md-12">
        <h6>
          <strong>Contract Services</strong>
        </h6>
        <hr />
        <input
          type="search"
          className="form-control input-sm"
          style={{ maxWidth: 260, marginBottom: 8 }}
          placeholder="Search for Services"
          value={state.search}
          onChange={(e) => setState({ search: e.target.value, start: 0 })}
        />
        {!table.data ? (
          <Spinner />
        ) : (
          <div style={{ maxHeight: 260, overflow: 'auto' }}>
            <table className="table table-striped table-bordered" style={{ width: '100%' }}>
              <thead>
                <tr>
                  <th>Name</th>
                  <th>Service Category</th>
                  <th>Priority</th>
                  <th>Coverage</th>
                  <th>Response SLA</th>
                  <th>Resolve SLA</th>
                  <th>Measurement Unit</th>
                  <th>Quantity</th>
                  <th>Cost</th>
                  <th className="a-right">Assign</th>
                </tr>
              </thead>
              <tbody>
                {rows.length === 0 ? (
                  <tr>
                    <td colSpan={10}>There are no Services</td>
                  </tr>
                ) : (
                  rows.map((row) => (
                    <tr key={row.service_id}>
                      <td>{row.service_name}</td>
                      <td>{row.service_category}</td>
                      <td>{row.priority_name}</td>
                      <td>{row.coverage_name}</td>
                      <td>{row.sla_name}</td>
                      <td>{row.resolve_sla_name}</td>
                      <td>{row.UM_name}</td>
                      <td>{row.quantity}</td>
                      <td>
                        {row.cost} {row.currency}
                      </td>
                      <td className="a-right">
                        <input
                          type="checkbox"
                          checked={row.assigned}
                          onChange={(e) => toggle(row.service_id, e.target.checked)}
                        />
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        )}
        {(table.data?.recordsFiltered ?? 0) > state.length && (
          <div className="text-center" style={{ marginTop: 6 }}>
            <button
              type="button"
              className="btn btn-default btn-xs"
              onClick={() => setState({ length: state.length + 25 })}
            >
              Show more
            </button>
          </div>
        )}
      </div>
    </div>
  )
}

/** Fereastra "Add New Template", cu tabelul de servicii de bifat. */
function NewTemplateModal({
  open,
  contractTypes,
  onClose,
  onSaved,
}: {
  open: boolean
  contractTypes: ContractTypeOption[]
  onClose(): void
  onSaved(): void
}) {
  const [name, setName] = useState('')
  const [description, setDescription] = useState('')
  const [typeId, setTypeId] = useState(0)
  const [status, setStatus] = useState('1')
  const [chosen, setChosen] = useState<number[]>([])
  const [search, setSearch] = useState('')

  const services = useQuery({
    ...templateServicesQuery(0, {
      start: 0,
      length: 200,
      orderColumn: 0,
      orderName: 'service_name',
      orderDir: 'asc',
      search,
    }),
    enabled: open,
  })

  useEffect(() => {
    if (!open) return
    setName('')
    setDescription('')
    setTypeId(0)
    setStatus('1')
    setChosen([])
    setSearch('')
  }, [open])

  const submit = (event: FormEvent) => {
    event.preventDefault()
    void saveContractTemplate(0, {
      name: name.trim(),
      description,
      contract_type: typeId,
      status,
      added_service: chosen,
    })
      .then(() => {
        toast('Contract Template created.', 'Success')
        onSaved()
      })
      .catch((error: unknown) => toast(failed(error), 'Error', 'error'))
  }

  return (
    <Modal
      id="newContractTemplateModal"
      labelledBy="newContractsTemplateModalLabel"
      open={open}
      onClose={onClose}
      large
      title="Add New Template"
    >
      <form method="post" onSubmit={submit}>
        <div className="row">
          <div className="col-md-12">
            <div className="form-group">
              <label className="control-label">
                Name <span className="required">*</span>
              </label>
              <input
                type="text"
                className="form-control input-sm"
                value={name}
                onChange={(e) => setName(e.target.value)}
                required
              />
            </div>
          </div>
          <div className="col-md-12">
            <div className="form-group">
              <label className="control-label">Description</label>
              <textarea
                className="form-control input-sm"
                value={description}
                onChange={(e) => setDescription(e.target.value)}
              />
            </div>
          </div>
          <div className="clearfix"></div>
          <div className="col-md-9">
            <div className="form-group">
              <label className="control-label">
                Contract Type<span className="required">*</span>
              </label>
              <select
                className="form-control input-sm"
                value={typeId}
                onChange={(e) => setTypeId(Number(e.target.value))}
              >
                <option value={0}> - select - </option>
                {contractTypes.map((item) => (
                  <option key={item.ID} value={item.ID}>
                    {item.name}
                  </option>
                ))}
              </select>
            </div>
          </div>
          <div className="col-md-3">
            <div className="form-group">
              <label className="control-label">
                Status <span className="required">*</span>
              </label>
              <select className="form-control input-sm" value={status} onChange={(e) => setStatus(e.target.value)}>
                <option value="1">Active</option>
                <option value="0">Inactive</option>
              </select>
            </div>
          </div>

          <div className="col-md-12">
            <input
              type="search"
              className="form-control input-sm"
              style={{ maxWidth: 260, marginBottom: 8 }}
              placeholder="Search for Services"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
            <div style={{ maxHeight: 200, overflow: 'auto' }}>
              <table className="table table-striped table-bordered" style={{ width: '100%' }}>
                <thead>
                  <tr>
                    <th>Name</th>
                    <th>Service Category</th>
                    <th>Priority</th>
                    <th>Coverage</th>
                    <th className="a-right">Assign</th>
                  </tr>
                </thead>
                <tbody>
                  {(services.data?.data ?? []).length === 0 ? (
                    <tr>
                      <td colSpan={5}>There are no Services</td>
                    </tr>
                  ) : (
                    (services.data?.data ?? []).map((row) => (
                      <tr key={row.service_id}>
                        <td>{row.service_name}</td>
                        <td>{row.service_category}</td>
                        <td>{row.priority_name}</td>
                        <td>{row.coverage_name}</td>
                        <td className="a-right">
                          <input
                            type="checkbox"
                            checked={chosen.includes(row.service_id)}
                            onChange={(e) =>
                              setChosen((prev) =>
                                e.target.checked
                                  ? [...prev, row.service_id]
                                  : prev.filter((id) => id !== row.service_id),
                              )
                            }
                          />
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </div>

          <div className="clearfix"></div>
          <div className="col-md-12">
            <div className="form-group aright">
              <button type="button" className="btn btn-default" onClick={onClose}>
                Close
              </button>{' '}
              <button type="submit" className="btn btn-primary" disabled={name.trim() === '' || typeId < 1}>
                Create
              </button>
            </div>
          </div>
        </div>
      </form>
    </Modal>
  )
}
